import React, { useCallback, useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  Connection,
  Edge,
  Node,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import { usePages } from "../store/PagesContext";
import "../styles/ui.css";

// Auto-layout via Dagre
function layout(nodes: Node[], edges: Edge[]) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", nodesep: 40, ranksep: 80 });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => g.setNode(n.id, { width: 220, height: 60 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  return nodes.map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: p.x - 110, y: p.y - 30 } };
  });
}

export default function GraphView() {
  const { pages } = usePages();

  // Build nodes/edges from pages
  const initialNodes: Node[] = useMemo(
    () =>
      pages.map((p, i) => ({
        id: p.name || `page-${i}`,
        data: { label: p.name || `Page ${i + 1}` },
        position: { x: 120 + (i % 3) * 260, y: 100 + Math.floor(i / 3) * 140 },
        style: { width: 220, height: 60, borderRadius: 12 },
        type: "default",
      })),
    [pages]
  );

  const initialEdges: Edge[] = useMemo(
    () =>
      pages.flatMap((p) =>
        (p.depends_on || []).map((dep) => ({
          id: `${dep}->${p.name}`,
          source: dep,
          target: p.name,
          animated: false,
          style: { strokeWidth: 2 },
        }))
      ),
    [pages]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onConnect = useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target || conn.source === conn.target) return;
      setEdges((eds) => addEdge({ ...conn, animated: false, style: { strokeWidth: 2 } }, eds));
      // Note: You can also persist to PagesContext.depends_on here if desired
    },
    [setEdges]
  );

  const onAutoLayout = useCallback(() => {
    const laidOut = layout(nodes, edges);
    setNodes(laidOut);
  }, [nodes, edges, setNodes]);

  return (
    <div className="h-screen flex flex-col">
      {/* Navbar */}
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto h-14 px-4 flex items-center justify-between">
          {/* Left: title */}
          <div className="flex items-center gap-3">
            <div className="text-lg font-semibold text-gray-800">Graph View</div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            <button className="btn-neutral" onClick={onAutoLayout}>Auto layout</button>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
          fitViewOptions={{ padding: 0.2 }}
        >
          <MiniMap pannable zoomable />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
    </div>
  );
}
